import { jwtDecode } from 'jwt-decode';
import { apiBaseUrl } from '../apiConsts';

import { getDefaultError } from '../../utils';
import { getDataWithRetry, sendWithoutBody } from '../fetchUtils';

import type { FetchResult } from '../fetchUtils';
import type { BaseApiResponse } from '../apiTypes';

export const sendVerificationCode = async (
  email: string,
): Promise<FetchResult<null>> => {
  if (!email) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No email provided'),
    };
  }
  return sendWithoutBody(`${apiBaseUrl}auth/send-code`, {
    headers: { 'content-type': 'application/json' },
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export interface VerificationCodeResponse extends BaseApiResponse {
  // New token-based auth fields
  accessToken?: string;
  refreshToken?: string;
  managerId?: string;
  trainerId?: string;
  memberId?: string;
}

enum LoginRole {
  MEMBER = 'ROLE_MEMBER',
  TRAINER = 'ROLE_TRAINER',
  MANAGER = 'ROLE_MANAGER',
}

interface LoginRoleData {
  role: LoginRole;
  id: string;
}

interface VerifyLoginJwtPayload {
  sub: string; //the email
  roles: LoginRoleData[];
  iat: number;
  exp: number;
}

export const verifyLoginCode = async (
  email: string,
  code: string,
): Promise<FetchResult<VerificationCodeResponse | null>> => {
  if (!email || !code) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No email or code provided'),
    };
  }

  // New response format: { accessToken, refreshToken }
  const isNewVerificationResponse = (
    payload: unknown,
  ): payload is { accessToken: string; refreshToken: string } => {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'accessToken' in payload &&
      'refreshToken' in payload &&
      typeof (payload as { accessToken?: unknown }).accessToken === 'string' &&
      typeof (payload as { refreshToken?: unknown }).refreshToken === 'string'
    );
  };

  const response = await getDataWithRetry<
    { accessToken: string; refreshToken: string }
  >(
    `${apiBaseUrl}auth/verify-code`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ email, code }),
    },
    isNewVerificationResponse,
    1, // retries
  );

  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error: response.error,
      status: response.status,
      durationMs: response.durationMs,
    };
  }

  const { accessToken, refreshToken } = response.data;
  const { roles }: VerifyLoginJwtPayload = jwtDecode(accessToken);
  const managerId = roles.find(({ role }) => role === LoginRole.MANAGER)?.id;
  const trainerId = roles.find(({ role }) => role === LoginRole.TRAINER)?.id;
  const memberId = roles.find(({ role }) => role === LoginRole.MEMBER)?.id;

  const parsedResponse: VerificationCodeResponse = {
    success: true,
    accessToken,
    refreshToken,
    managerId,
    trainerId,
    memberId,
  };

  return {
    success: true,
    data: parsedResponse,
    status: response.status,
    durationMs: response.durationMs,
  };
};

/**
 * Refresh token exchange
 * POST /v2/api/auth/refresh
 * Exchange a refresh token for a new access + refresh token pair
 */
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export const refreshTokens = async (
  refreshToken: string,
): Promise<FetchResult<RefreshTokenResponse | null>> => {
  if (!refreshToken) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Refresh token is required'),
    };
  }

  const isRefreshTokenResponse = (
    payload: unknown,
  ): payload is RefreshTokenResponse => {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'accessToken' in payload &&
      'refreshToken' in payload &&
      typeof (payload as any).accessToken === 'string' &&
      typeof (payload as any).refreshToken === 'string'
    );
  };

  const response = await getDataWithRetry<RefreshTokenResponse>(
    `${apiBaseUrl}auth/refresh`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    },
    isRefreshTokenResponse,
    1, // retries
  );

  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error: response.error,
      status: response.status,
      durationMs: response.durationMs,
    };
  }

  return {
    success: true,
    data: response.data,
    status: response.status,
    durationMs: response.durationMs,
  };
};

/**
 * Logout endpoint
 * POST /v2/api/auth/logout
 * Invalidates the refresh token session for a specific role identity
 * Requires Authorization: Bearer <accessToken> header
 */
export interface LogoutResponse {
  message: string;
}

export const logout = async (
  accessToken: string,
  sessionType: 'member' | 'trainer' | 'manager',
  sessionId: string,
): Promise<FetchResult<LogoutResponse | null>> => {
  if (!accessToken || !sessionType || !sessionId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Access token, session type, and session ID are required'),
    };
  }

  const isLogoutResponse = (payload: unknown): payload is LogoutResponse => {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof (payload as any).message === 'string'
    );
  };

  const response = await getDataWithRetry<LogoutResponse>(
    `${apiBaseUrl}v2/api/auth/logout`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        type: sessionType,
        id: sessionId,
      }),
    },
    isLogoutResponse,
  );

  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error: response.error,
      status: response.status,
      durationMs: response.durationMs,
    };
  }

  return {
    success: true,
    data: response.data,
    status: response.status,
    durationMs: response.durationMs,
  };
};
